import React, { useState, useEffect } from 'react';
import { authAPI, projectsAPI, blogAPI, aboutAPI, skillsAPI } from '../utils/api';
import '../styles/Admin.css';

function Admin({ isLoggedIn, setIsLoggedIn }) {
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState(null);
  const [activeTab, setActiveTab] = useState('about');
  
  // About
  const [about, setAbout] = useState(null);
  const [editingAbout, setEditingAbout] = useState(false);
  
  // Skills
  const [skills, setSkills] = useState(null);
  const [editingSkills, setEditingSkills] = useState(false);
  
  // Projects
  const [projects, setProjects] = useState([]);
  const [newProject, setNewProject] = useState({
    title: '',
    description: '',
    technologies: [],
    github: '',
    demo: '',
  });
  
  // Blog
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState({
    title: '',
    excerpt: '',
    content: '',
    tags: [],
  });

  // Load data on mount
  useEffect(() => {
    if (isLoggedIn) {
      loadAllData();
    }
  }, [isLoggedIn]);

  const loadAllData = async () => {
    try {
      const [aboutRes, skillsRes, projectsRes, blogRes] = await Promise.all([
        aboutAPI.get(),
        skillsAPI.get(),
        projectsAPI.getAll(),
        blogAPI.getAll(),
      ]);
      setAbout(aboutRes.data);
      setSkills(skillsRes.data);
      setProjects(projectsRes.data);
      setPosts(blogRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(null);

    try {
      const response = await authAPI.login(password);
      localStorage.setItem('token', response.data.token);
      setIsLoggedIn(true);
      setPassword('');
      loadAllData();
    } catch (error) {
      setLoginError('密码错误');
    }
  };

  // About handlers
  const handleUpdateAbout = async (e) => {
    e.preventDefault();
    try {
      await aboutAPI.update(about);
      setEditingAbout(false);
      alert('个人信息已更新！');
    } catch (error) {
      alert('更新失败：' + error.message);
    }
  };

  // Skills handlers
  const handleUpdateSkills = async (e) => {
    e.preventDefault();
    try {
      await skillsAPI.update(skills);
      setEditingSkills(false);
      alert('技能已更新！');
    } catch (error) {
      alert('更新失败：' + error.message);
    }
  };

  // Projects handlers
  const handleAddProject = async (e) => {
    e.preventDefault();
    try {
      await projectsAPI.create(newProject);
      setNewProject({
        title: '',
        description: '',
        technologies: [],
        github: '',
        demo: '',
      });
      loadAllData();
      alert('项目已添加！');
    } catch (error) {
      alert('添加失败：' + error.message);
    }
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('确定删除该项目？')) {
      try {
        await projectsAPI.delete(id);
        loadAllData();
      } catch (error) {
        alert('删除失败：' + error.message);
      }
    }
  };

  // Blog handlers
  const handleAddPost = async (e) => {
    e.preventDefault();
    try {
      await blogAPI.create(newPost);
      setNewPost({
        title: '',
        excerpt: '',
        content: '',
        tags: [],
      });
      loadAllData();
      alert('文章已发布！');
    } catch (error) {
      alert('发布失败：' + error.message);
    }
  };

  const handleDeletePost = async (id) => {
    if (window.confirm('确定删除该文章？')) {
      try {
        await blogAPI.delete(id);
        loadAllData();
      } catch (error) {
        alert('删除失败：' + error.message);
      }
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="page-container admin-page">
        <div className="login-container card">
          <h1>管理员登录</h1>
          {loginError && <div className="error-message">{loginError}</div>}
          
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="password">密码</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="请输入管理员密码"
                required
              />
            </div>
            <button type="submit" className="btn btn-success">
              登录
            </button>
          </form>
          
          <p className="login-hint">
            默认密码是 admin123，请在 server.js 中修改。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container admin-page">
      <h1>管理面板</h1>
      
      <div className="admin-tabs">
        <button 
          className={`tab ${activeTab === 'about' ? 'active' : ''}`}
          onClick={() => setActiveTab('about')}
        >
          个人信息
        </button>
        <button 
          className={`tab ${activeTab === 'skills' ? 'active' : ''}`}
          onClick={() => setActiveTab('skills')}
        >
          技能
        </button>
        <button 
          className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          项目
        </button>
        <button 
          className={`tab ${activeTab === 'blog' ? 'active' : ''}`}
          onClick={() => setActiveTab('blog')}
        >
          博客
        </button>
      </div>

      {/* About Tab */}
      {activeTab === 'about' && about && (
        <div className="admin-section card">
          <h2>个人信息</h2>
          {!editingAbout ? (
            <>
              <p><strong>名字:</strong> {about.name}</p>
              <p><strong>职位:</strong> {about.title}</p>
              <p><strong>简介:</strong> {about.bio}</p>
              <p><strong>邮箱:</strong> {about.email}</p>
              <p><strong>电话:</strong> {about.phone}</p>
              <p><strong>地点:</strong> {about.location}</p>
              <button 
                className="btn" 
                onClick={() => setEditingAbout(true)}
              >
                编辑
              </button>
            </>
          ) : (
            <form onSubmit={handleUpdateAbout}>
              <div className="form-group">
                <label>名字</label>
                <input
                  type="text"
                  value={about.name}
                  onChange={(e) => setAbout({ ...about, name: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>职位</label>
                <input
                  type="text"
                  value={about.title}
                  onChange={(e) => setAbout({ ...about, title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>简介</label>
                <textarea
                  value={about.bio}
                  onChange={(e) => setAbout({ ...about, bio: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>邮箱</label>
                <input
                  type="email"
                  value={about.email}
                  onChange={(e) => setAbout({ ...about, email: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>电话</label>
                <input
                  type="tel"
                  value={about.phone}
                  onChange={(e) => setAbout({ ...about, phone: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>地点</label>
                <input
                  type="text"
                  value={about.location}
                  onChange={(e) => setAbout({ ...about, location: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-success">保存</button>
              <button 
                type="button"
                className="btn"
                onClick={() => setEditingAbout(false)}
              >
                取消
              </button>
            </form>
          )}
        </div>
      )}

      {/* Skills Tab */}
      {activeTab === 'skills' && skills && (
        <div className="admin-section card">
          <h2>技能</h2>
          {!editingSkills ? (
            <>
              {skills.categories && skills.categories.map((cat, idx) => (
                <div key={idx}>
                  <strong>{cat.category}:</strong> {cat.items.join(', ')}
                </div>
              ))}
              <button 
                className="btn"
                onClick={() => setEditingSkills(true)}
              >
                编辑
              </button>
            </>
          ) : (
            <form onSubmit={handleUpdateSkills}>
              {skills.categories && skills.categories.map((cat, idx) => (
                <div key={idx} className="form-group">
                  <label>{cat.category}</label>
                  <input
                    type="text"
                    value={cat.items.join(', ')}
                    onChange={(e) => {
                      const updated = [...skills.categories];
                      updated[idx].items = e.target.value.split(',').map(s => s.trim());
                      setSkills({ ...skills, categories: updated });
                    }}
                  />
                </div>
              ))}
              <button type="submit" className="btn btn-success">保存</button>
              <button 
                type="button"
                className="btn"
                onClick={() => setEditingSkills(false)}
              >
                取消
              </button>
            </form>
          )}
        </div>
      )}

      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div>
          <div className="admin-section card">
            <h2>添加新项目</h2>
            <form onSubmit={handleAddProject}>
              <div className="form-group">
                <label>项目标题</label>
                <input
                  type="text"
                  value={newProject.title}
                  onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>项目描述</label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>技术栈 (逗号分隔)</label>
                <input
                  type="text"
                  value={newProject.technologies.join(', ')}
                  onChange={(e) => setNewProject({ 
                    ...newProject, 
                    technologies: e.target.value.split(',').map(s => s.trim()) 
                  })}
                />
              </div>
              <div className="form-group">
                <label>GitHub 链接</label>
                <input
                  type="url"
                  value={newProject.github}
                  onChange={(e) => setNewProject({ ...newProject, github: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Demo 链接</label>
                <input
                  type="url"
                  value={newProject.demo}
                  onChange={(e) => setNewProject({ ...newProject, demo: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-success">添加项目</button>
            </form>
          </div>

          <div className="admin-section card">
            <h2>现有项目</h2>
            {projects.length === 0 ? (
              <p>还没有项目</p>
            ) : (
              <div className="projects-list">
                {projects.map((project) => (
                  <div key={project.id} className="project-item">
                    <h3>{project.title}</h3>
                    <p>{project.description}</p>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      删除
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blog Tab */}
      {activeTab === 'blog' && (
        <div>
          <div className="admin-section card">
            <h2>发布新文章</h2>
            <form onSubmit={handleAddPost}>
              <div className="form-group">
                <label>文章标题</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>摘要</label>
                <input
                  type="text"
                  value={newPost.excerpt}
                  onChange={(e) => setNewPost({ ...newPost, excerpt: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>内容</label>
                <textarea
                  value={newPost.content}
                  onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                  required
                  rows="10"
                />
              </div>
              <div className="form-group">
                <label>标签 (逗号分隔)</label>
                <input
                  type="text"
                  value={newPost.tags.join(', ')}
                  onChange={(e) => setNewPost({ 
                    ...newPost, 
                    tags: e.target.value.split(',').map(s => s.trim()) 
                  })}
                />
              </div>
              <button type="submit" className="btn btn-success">发布</button>
            </form>
          </div>

          <div className="admin-section card">
            <h2>现有文章</h2>
            {posts.length === 0 ? (
              <p>还没有文章</p>
            ) : (
              <div className="posts-list">
                {posts.map((post) => (
                  <div key={post.id} className="post-item">
                    <h3>{post.title}</h3>
                    <p>{post.excerpt || post.content.substring(0, 100)}</p>
                    <button 
                      className="btn btn-danger"
                      onClick={() => handleDeletePost(post.id)}
                    >
                      删除
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
